import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { getDialogMaxHeight } from '@/utils/viewport';
import { CentralizedMobileDetection } from '@/hooks/useCentralizedMobileDetection';

interface UseDialogThemeProps {
  isExpanded?: boolean;
  gestureActive?: boolean;
  gestureDirection?: 'up' | 'down' | 'left' | 'right';
  className?: string;
  dialogPosition?: 'bottom' | 'center' | 'fullscreen';
  detection?: CentralizedMobileDetection;
}

const useDialogTheme = ({
  isExpanded = false,
  gestureActive = false,
  gestureDirection,
  className,
  dialogPosition = 'bottom',
  detection
}: UseDialogThemeProps) => {
  
  const dialogClasses = useMemo(() => {
    // Device-aware styling based on position and device type
    const isCenteredModal = dialogPosition === 'center';
    const isDesktop = detection ? !detection.isMobile : true;
    
    return cn(
      // Base dialog styles (positioning handled by useDialogSizing)
      'bg-background border shadow-lg transition-all duration-300 ease-out',
      
      // Device and position aware width constraints
      isDesktop && isCenteredModal 
        ? 'w-auto' // Let useDialogSizing handle width for desktop modals
        : 'w-full max-w-lg', // Keep current mobile behavior
      
      // Device and position aware border radius
      isDesktop && isCenteredModal
        ? 'rounded-lg' // Standard modal corners for desktop
        : 'rounded-t-2xl border-t', // Bottom sheet style for mobile/bottom position
      
      // Device and position aware transform origin
      isDesktop && isCenteredModal
        ? 'origin-center' // Center origin for desktop modals
        : 'origin-bottom', // Bottom origin for mobile bottom sheets
      
      // Gesture feedback with smooth transitions
      gestureActive && 'transition-transform duration-150',
      gestureDirection === 'down' && gestureActive && 'translate-y-2',
      gestureDirection === 'up' && gestureActive && '-translate-y-2',
      gestureDirection === 'left' && gestureActive && '-translate-x-2',
      gestureDirection === 'right' && gestureActive && 'translate-x-2',
      
      // Custom className
      className
    );
  }, [isExpanded, gestureActive, gestureDirection, className, dialogPosition, detection?.isMobile]);

  return {
    dialogClasses
  };
};

export default useDialogTheme;
export { useDialogTheme };
